// FILE: src/components/PaceAnalytics.js
//
// End-of-exam time-management analytics (Phase 6), shown on ResultsPage beside
// the phase/domain performance. Presentational: it renders a
// computePaceAnalytics() object — metric tiles, a plain-language coaching
// summary, and a lightweight, dependency-free pace timeline (per-question pace
// across five segments vs the target rate). Results-only in v1 (D3).
//
// Design: docs/pace-coaching-proposal.md §6–§7.

import React from 'react';
import css from './PaceAnalytics.module.css';
import { paceResultsSummary } from '../../services/paceService';

/** Per-question duration: "20s" or "1m 04s". */
function fmtPerQ(sec) {
  const s = Math.round(sec || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

/** Coarse duration in minutes/hours: "18 min" or "1h 05m". */
function fmtMins(sec) {
  const m = Math.round((sec || 0) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${String(mm).padStart(2, '0')}m`;
}

function Metric({ label, value, accent }) {
  return (
    <div className={css.metric}>
      <div className={`${css.metricValue} ${accent ? css[accent] : ''}`}>{value}</div>
      <div className={css.metricLabel}>{label}</div>
    </div>
  );
}

function Timeline({ trend, target }) {
  if (!Array.isArray(trend) || trend.length === 0) return null;
  const max = Math.max(target || 0, ...trend) * 1.15 || 1;
  const targetPct = target > 0 ? Math.round((target / max) * 100) : 0;
  return (
    <div className={css.timeline} data-testid="pace-timeline" role="img" aria-label="Pace per question across the exam">
      <div className={css.bars}>
        {trend.map((v, i) => {
          const h = Math.max(3, Math.round((v / max) * 100));
          const slow = target > 0 && v > target;
          return (
            <div key={i} className={css.barCell}>
              <div
                className={`${css.bar} ${slow ? css.barSlow : css.barFast}`}
                style={{ height: `${h}%` }}
                title={`${Math.round(v)}s / question`}
              />
            </div>
          );
        })}
        {target > 0 && (
          <div className={css.targetLine} style={{ bottom: `${targetPct}%` }} aria-hidden="true">
            <span className={css.targetLabel}>target {fmtPerQ(target)}</span>
          </div>
        )}
      </div>
      <div className={css.axis}><span>Start</span><span>End</span></div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.analytics  output of paceService.computePaceAnalytics()
 */
export default function PaceAnalytics({ analytics }) {
  if (!analytics || !analytics.answered) return null;

  const {
    avgSecondsPerQuestion, fastestQuestionSeconds, longestQuestionSeconds,
    completionMarginSeconds, finishedAhead, flaggedCount, answerChangeCount,
    trend, targetSecPerQ,
  } = analytics;

  return (
    <div className={css.wrap} data-testid="pace-analytics">
      <h3 className={css.title}>Time management</h3>
      <p className={css.summary}>{paceResultsSummary(analytics)}</p>

      <div className={css.metrics}>
        <Metric label="Avg / question" value={fmtPerQ(avgSecondsPerQuestion)} />
        <Metric label="Fastest" value={fmtPerQ(fastestQuestionSeconds)} />
        <Metric label="Longest" value={fmtPerQ(longestQuestionSeconds)} />
        <Metric
          label={finishedAhead ? 'Review buffer' : 'Over time'}
          value={fmtMins(Math.abs(completionMarginSeconds))}
          accent={finishedAhead ? 'good' : 'warn'}
        />
        <Metric label="Flagged" value={flaggedCount} />
        <Metric label="Answer changes" value={answerChangeCount} />
      </div>

      <Timeline trend={trend} target={targetSecPerQ} />
    </div>
  );
}
